import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  suppliersApi,
} from "../api/suppliersApi";

import type {
  CreateSupplierInput,
  PaymentTermsType,
  SupplierRecord,
  SupplierType,
  SuppliersSort,
} from "../types/suppliers.types";

import "../screenstyles/Suppliers.css";


type SupplierFormState = {
  id: string;

  code: string;

  name: string;

  nameAr: string;

  type: SupplierType;

  contactPerson: string;

  phone: string;

  alternatePhone: string;

  email: string;

  country: string;

  province: string;

  city: string;

  address: string;

  taxNumber: string;

  registrationNumber: string;

  currencyCode: string;

  paymentTermsType:
    PaymentTermsType;

  paymentTermDays: string;

  creditLimit: string;

  bankName: string;

  bankAccountName: string;

  bankAccountNumber: string;

  iban: string;

  swiftCode: string;

  notes: string;

  isActive: boolean;
};


const EMPTY_FORM:
  SupplierFormState = {
    id: "",

    code: "",

    name: "",

    nameAr: "",

    type: "local",

    contactPerson: "",

    phone: "",

    alternatePhone: "",

    email: "",

    country: "Iraq",

    province: "",

    city: "",

    address: "",

    taxNumber: "",

    registrationNumber: "",

    currencyCode: "IQD",

    paymentTermsType: "cash",

    paymentTermDays: "0",

    creditLimit: "",

    bankName: "",

    bankAccountName: "",

    bankAccountNumber: "",

    iban: "",

    swiftCode: "",

    notes: "",

    isActive: true,
  };


const COMMON_CURRENCIES = [
  "IQD",
  "USD",
  "EUR",
  "CNY",
  "AED",
  "SAR",
  "TRY",
  "GBP",
];


function nullableText(
  value: string,
): string | null {
  const normalized =
    value.trim();

  return normalized || null;
}


function getErrorMessage(
  error: unknown,
): string {
  if (
    error &&
    typeof error === "object"
  ) {
    const candidate =
      error as {
        message?: unknown;
      };

    if (
      typeof candidate.message ===
      "string"
    ) {
      return candidate.message;
    }
  }

  return "حدث خطأ غير متوقع.";
}


function supplierToForm(
  supplier: SupplierRecord,
): SupplierFormState {
  return {
    id:
      supplier.id,

    code:
      supplier.code,

    name:
      supplier.name,

    nameAr:
      supplier.nameAr ?? "",

    type:
      supplier.type,

    contactPerson:
      supplier.contactPerson ?? "",

    phone:
      supplier.phone ?? "",

    alternatePhone:
      supplier.alternatePhone ?? "",

    email:
      supplier.email ?? "",

    country:
      supplier.country || "Iraq",

    province:
      supplier.province ?? "",

    city:
      supplier.city ?? "",

    address:
      supplier.address ?? "",

    taxNumber:
      supplier.taxNumber ?? "",

    registrationNumber:
      supplier.registrationNumber ??
      "",

    currencyCode:
      supplier.currencyCode ||
      "IQD",

    paymentTermsType:
      supplier.paymentTermsType,

    paymentTermDays:
      String(
        supplier.paymentTermDays ??
          0,
      ),

    creditLimit:
      supplier.creditLimit ===
      null
        ? ""
        : String(
            supplier.creditLimit,
          ),

    bankName:
      supplier.bankName ?? "",

    bankAccountName:
      supplier.bankAccountName ??
      "",

    bankAccountNumber:
      supplier.bankAccountNumber ??
      "",

    iban:
      supplier.iban ?? "",

    swiftCode:
      supplier.swiftCode ?? "",

    notes:
      supplier.notes ?? "",

    isActive:
      supplier.isActive,
  };
}


export default function Suppliers() {
  const [
    suppliers,
    setSuppliers,
  ] = useState<SupplierRecord[]>(
    [],
  );

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    deletingId,
    setDeletingId,
  ] = useState<string | null>(
    null,
  );

  const [
    error,
    setError,
  ] = useState("");

  const [
    successMessage,
    setSuccessMessage,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    typeFilter,
    setTypeFilter,
  ] = useState<
    "" | SupplierType
  >("");

  const [
    activeFilter,
    setActiveFilter,
  ] = useState<
    "active" | "inactive" | "all"
  >("active");

  const [
    currencyFilter,
    setCurrencyFilter,
  ] = useState("");

  const [
    sort,
    setSort,
  ] = useState<SuppliersSort>(
    "name_asc",
  );

  const [
    nextCursor,
    setNextCursor,
  ] = useState<string | null>(
    null,
  );

  const [
    loadingMore,
    setLoadingMore,
  ] = useState(false);

  const [
    formOpen,
    setFormOpen,
  ] = useState(false);

  const [
    editingSupplier,
    setEditingSupplier,
  ] = useState<SupplierRecord | null>(
    null,
  );

  const [
    form,
    setForm,
  ] = useState<SupplierFormState>(
    EMPTY_FORM,
  );


  const loadSuppliers =
    useCallback(
      async (
        append = false,
        cursor?: string,
      ) => {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        setError("");

        try {
          const result =
            await suppliersApi.list({
              search:
                search.trim() ||
                undefined,

              type:
                typeFilter ||
                undefined,

              currencyCode:
                currencyFilter.trim()
                  ? currencyFilter
                      .trim()
                      .toUpperCase()
                  : undefined,

              isActive:
                activeFilter ===
                "all"
                  ? undefined
                  : activeFilter ===
                    "active",

              sort,

              cursor,

              limit: 30,
            });

          setSuppliers(
            current =>
              append
                ? [
                    ...current,
                    ...result.items,
                  ]
                : result.items,
          );

          setNextCursor(
            result.pagination
              .nextCursor,
          );
        } catch (loadError) {
          setError(
            getErrorMessage(
              loadError,
            ),
          );
        } finally {
          setLoading(false);
          setLoadingMore(false);
        }
      },
      [
        activeFilter,
        currencyFilter,
        search,
        sort,
        typeFilter,
      ],
    );


  useEffect(() => {
    const timer =
      window.setTimeout(
        () => {
          void loadSuppliers();
        },
        300,
      );

    return () => {
      window.clearTimeout(
        timer,
      );
    };
  }, [loadSuppliers]);


  const statistics =
    useMemo(
      () => {
        const active =
          suppliers.filter(
            supplier =>
              supplier.isActive,
          ).length;

        const local =
          suppliers.filter(
            supplier =>
              supplier.type ===
              "local",
          ).length;

        const international =
          suppliers.filter(
            supplier =>
              supplier.type ===
              "international",
          ).length;

        return {
          total:
            suppliers.length,

          active,

          local,

          international,
        };
      },
      [suppliers],
    );


  function updateForm<
    K extends keyof SupplierFormState,
  >(
    key: K,
    value: SupplierFormState[K],
  ) {
    setForm(
      current => ({
        ...current,
        [key]: value,
      }),
    );
  }


  function openCreateForm() {
    setEditingSupplier(null);

    setForm({
      ...EMPTY_FORM,
    });

    setError("");
    setSuccessMessage("");

    setFormOpen(true);
  }


  function openEditForm(
    supplier: SupplierRecord,
  ) {
    setEditingSupplier(
      supplier,
    );

    setForm(
      supplierToForm(
        supplier,
      ),
    );

    setError("");
    setSuccessMessage("");

    setFormOpen(true);
  }


  function closeForm() {
    if (saving) {
      return;
    }

    setFormOpen(false);

    setEditingSupplier(null);

    setForm({
      ...EMPTY_FORM,
    });
  }


  async function handleSubmit(
    event: FormEvent,
  ) {
    event.preventDefault();

    if (
      !form.code.trim() ||
      !form.name.trim()
    ) {
      setError(
        "كود المورد واسم المورد مطلوبان.",
      );

      return;
    }

    if (
      !editingSupplier &&
      !form.id.trim()
    ) {
      setError(
        "معرّف المورد مطلوب.",
      );

      return;
    }

    const paymentTermDays =
      Number(
        form.paymentTermDays,
      );

    if (
      !Number.isInteger(
        paymentTermDays,
      ) ||
      paymentTermDays < 0
    ) {
      setError(
        "عدد أيام الاستحقاق غير صحيح.",
      );

      return;
    }

    if (
      form.paymentTermsType ===
        "cash" &&
      paymentTermDays !== 0
    ) {
      setError(
        "المورد النقدي يجب أن تكون أيام الاستحقاق له 0.",
      );

      return;
    }

    let creditLimit:
      number | null = null;

    if (
      form.creditLimit.trim()
    ) {
      creditLimit =
        Number(
          form.creditLimit,
        );

      if (
        !Number.isFinite(
          creditLimit,
        ) ||
        creditLimit < 0
      ) {
        setError(
          "حد الائتمان غير صحيح.",
        );

        return;
      }
    }

    setSaving(true);

    setError("");
    setSuccessMessage("");

    try {
      const commonInput = {
        code:
          form.code
            .trim()
            .toUpperCase(),

        name:
          form.name.trim(),

        nameAr:
          nullableText(
            form.nameAr,
          ),

        type:
          form.type,

        contactPerson:
          nullableText(
            form.contactPerson,
          ),

        phone:
          nullableText(
            form.phone,
          ),

        alternatePhone:
          nullableText(
            form.alternatePhone,
          ),

        email:
          nullableText(
            form.email,
          ),

        country:
          form.country.trim() ||
          "Iraq",

        province:
          nullableText(
            form.province,
          ),

        city:
          nullableText(
            form.city,
          ),

        address:
          nullableText(
            form.address,
          ),

        taxNumber:
          nullableText(
            form.taxNumber,
          ),

        registrationNumber:
          nullableText(
            form.registrationNumber,
          ),

        currencyCode:
          form.currencyCode
            .trim()
            .toUpperCase(),

        paymentTermsType:
          form.paymentTermsType,

        paymentTermDays,

        creditLimit,

        bankName:
          nullableText(
            form.bankName,
          ),

        bankAccountName:
          nullableText(
            form.bankAccountName,
          ),

        bankAccountNumber:
          nullableText(
            form.bankAccountNumber,
          ),

        iban:
          nullableText(
            form.iban,
          ),

        swiftCode:
          nullableText(
            form.swiftCode,
          ),

        notes:
          nullableText(
            form.notes,
          ),

        isActive:
          form.isActive,
      };

      if (editingSupplier) {
        await suppliersApi.update(
          editingSupplier.id,
          commonInput,
        );

        setSuccessMessage(
          "تم تحديث المورد بنجاح.",
        );
      } else {
        const input:
          CreateSupplierInput = {
            id:
              form.id
                .trim()
                .toLowerCase(),

            ...commonInput,
          };

        await suppliersApi.create(
          input,
        );

        setSuccessMessage(
          "تمت إضافة المورد بنجاح.",
        );
      }

      setFormOpen(false);

      setEditingSupplier(null);

      setForm({
        ...EMPTY_FORM,
      });

      await loadSuppliers();
    } catch (saveError) {
      setError(
        getErrorMessage(
          saveError,
        ),
      );
    } finally {
      setSaving(false);
    }
  }


  async function handleDelete(
    supplier: SupplierRecord,
  ) {
    const confirmed =
      window.confirm(
        `هل أنت متأكد من تعطيل المورد "${supplier.name}"؟`,
      );

    if (!confirmed) {
      return;
    }

    setDeletingId(
      supplier.id,
    );

    setError("");
    setSuccessMessage("");

    try {
      await suppliersApi.remove(
        supplier.id,
      );

      setSuccessMessage(
        "تم تعطيل المورد بنجاح.",
      );

      await loadSuppliers();
    } catch (deleteError) {
      setError(
        getErrorMessage(
          deleteError,
        ),
      );
    } finally {
      setDeletingId(null);
    }
  }


  async function handleReactivate(
    supplier: SupplierRecord,
  ) {
    setDeletingId(
      supplier.id,
    );

    setError("");
    setSuccessMessage("");

    try {
      await suppliersApi.update(
        supplier.id,
        {
          isActive: true,
        },
      );

      setSuccessMessage(
        "تمت إعادة تفعيل المورد بنجاح.",
      );

      await loadSuppliers();
    } catch (activateError) {
      setError(
        getErrorMessage(
          activateError,
        ),
      );
    } finally {
      setDeletingId(null);
    }
  }


  return (
    <div className="suppliers-page">
      <div className="suppliers-header">
        <div>
          <h1>
            إدارة الموردين
          </h1>

          <p>
            إدارة الموردين وبيانات
            الاتصال والعملات وشروط
            الدفع.
          </p>
        </div>

        <button
          type="button"
          className="suppliers-primary-button"
          onClick={
            openCreateForm
          }
        >
          + إضافة مورد
        </button>
      </div>


      <div className="suppliers-statistics">
        <div className="supplier-stat-card">
          <span>
            الموردون الظاهرون
          </span>

          <strong>
            {statistics.total}
          </strong>
        </div>

        <div className="supplier-stat-card">
          <span>
            النشطون
          </span>

          <strong>
            {statistics.active}
          </strong>
        </div>

        <div className="supplier-stat-card">
          <span>
            محليون
          </span>

          <strong>
            {statistics.local}
          </strong>
        </div>

        <div className="supplier-stat-card">
          <span>
            دوليون
          </span>

          <strong>
            {
              statistics.international
            }
          </strong>
        </div>
      </div>


      <div className="suppliers-toolbar">
        <input
          type="search"
          placeholder="البحث بالاسم، الكود، الهاتف، البريد..."
          value={search}
          onChange={
            event =>
              setSearch(
                event.target.value,
              )
          }
        />

        <select
          value={typeFilter}
          onChange={
            event =>
              setTypeFilter(
                event.target
                  .value as
                  | ""
                  | SupplierType,
              )
          }
        >
          <option value="">
            جميع الأنواع
          </option>

          <option value="local">
            محلي
          </option>

          <option value="international">
            دولي
          </option>
        </select>

        <select
          value={activeFilter}
          onChange={
            event =>
              setActiveFilter(
                event.target
                  .value as
                  | "active"
                  | "inactive"
                  | "all",
              )
          }
        >
          <option value="active">
            الموردون النشطون
          </option>

          <option value="inactive">
            الموردون المعطلون
          </option>

          <option value="all">
            الجميع
          </option>
        </select>

        <input
          className="supplier-currency-filter"
          value={currencyFilter}
          placeholder="العملة"
          maxLength={3}
          onChange={
            event =>
              setCurrencyFilter(
                event.target.value
                  .toUpperCase(),
              )
          }
        />

        <select
          value={sort}
          onChange={
            event =>
              setSort(
                event.target
                  .value as
                  SuppliersSort,
              )
          }
        >
          <option value="name_asc">
            الاسم تصاعديًا
          </option>

          <option value="name_desc">
            الاسم تنازليًا
          </option>

          <option value="code_asc">
            الكود تصاعديًا
          </option>

          <option value="code_desc">
            الكود تنازليًا
          </option>

          <option value="createdAt_desc">
            الأحدث
          </option>

          <option value="createdAt_asc">
            الأقدم
          </option>
        </select>
      </div>


      {error && (
        <div className="suppliers-alert suppliers-alert-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="suppliers-alert suppliers-alert-success">
          {successMessage}
        </div>
      )}


      <div className="suppliers-table-card">
        {loading ? (
          <div className="suppliers-empty">
            جاري تحميل الموردين...
          </div>
        ) : suppliers.length === 0 ? (
          <div className="suppliers-empty">
            لا توجد بيانات موردين.
          </div>
        ) : (
          <div className="suppliers-table-wrapper">
            <table className="suppliers-table">
              <thead>
                <tr>
                  <th>
                    الكود
                  </th>

                  <th>
                    المورد
                  </th>

                  <th>
                    النوع
                  </th>

                  <th>
                    الاتصال
                  </th>

                  <th>
                    الموقع
                  </th>

                  <th>
                    العملة
                  </th>

                  <th>
                    شروط الدفع
                  </th>

                  <th>
                    الحالة
                  </th>

                  <th>
                    الإجراءات
                  </th>
                </tr>
              </thead>

              <tbody>
                {suppliers.map(
                  supplier => (
                    <tr
                      key={
                        supplier.id
                      }
                    >
                      <td>
                        <strong>
                          {
                            supplier.code
                          }
                        </strong>
                      </td>

                      <td>
                        <div className="supplier-name-cell">
                          <strong>
                            {
                              supplier.nameAr ||
                              supplier.name
                            }
                          </strong>

                          {supplier.nameAr && (
                            <span>
                              {
                                supplier.name
                              }
                            </span>
                          )}

                          <small>
                            {
                              supplier.id
                            }
                          </small>
                        </div>
                      </td>

                      <td>
                        {supplier.type ===
                        "local"
                          ? "محلي"
                          : "دولي"}
                      </td>

                      <td>
                        <div className="supplier-contact-cell">
                          <span>
                            {
                              supplier.contactPerson ||
                              "-"
                            }
                          </span>

                          <small>
                            {
                              supplier.phone ||
                              "-"
                            }
                          </small>
                        </div>
                      </td>

                      <td>
                        {[
                          supplier.country,
                          supplier.province,
                          supplier.city,
                        ]
                          .filter(Boolean)
                          .join(" - ") ||
                          "-"}
                      </td>

                      <td>
                        <span className="supplier-currency-badge">
                          {
                            supplier.currencyCode
                          }
                        </span>
                      </td>

                      <td>
                        {supplier.paymentTermsType ===
                        "cash"
                          ? "نقدي"
                          : supplier.paymentTermsType ===
                            "credit"
                          ? `آجل - ${supplier.paymentTermDays} يوم`
                          : supplier.paymentTermsType ===
                            "advance"
                          ? "دفع مقدم"
                          : "مخصص"}
                      </td>

                      <td>
                        <span
                          className={
                            supplier.isActive
                              ? "supplier-status supplier-status-active"
                              : "supplier-status supplier-status-inactive"
                          }
                        >
                          {supplier.isActive
                            ? "نشط"
                            : "معطل"}
                        </span>
                      </td>

                      <td>
                        <div className="supplier-actions">
                          <button
                            type="button"
                            onClick={() =>
                              openEditForm(
                                supplier,
                              )
                            }
                          >
                            تعديل
                          </button>

                          {supplier.isActive ? (
                            <button
                              type="button"
                              className="supplier-danger-button"
                              disabled={
                                deletingId ===
                                supplier.id
                              }
                              onClick={() =>
                                void handleDelete(
                                  supplier,
                                )
                              }
                            >
                              تعطيل
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="supplier-success-button"
                              disabled={
                                deletingId ===
                                supplier.id
                              }
                              onClick={() =>
                                void handleReactivate(
                                  supplier,
                                )
                              }
                            >
                              تفعيل
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {nextCursor && (
        <div className="suppliers-load-more">
          <button
            type="button"
            disabled={loadingMore}
            onClick={() =>
              void loadSuppliers(
                true,
                nextCursor,
              )
            }
          >
            {loadingMore
              ? "جاري التحميل..."
              : "تحميل المزيد"}
          </button>
        </div>
      )}


      {formOpen && (
        <div
          className="supplier-modal-backdrop"
          onMouseDown={
            event => {
              if (
                event.target ===
                event.currentTarget
              ) {
                closeForm();
              }
            }
          }
        >
          <div className="supplier-modal">
            <div className="supplier-modal-header">
              <div>
                <h2>
                  {editingSupplier
                    ? "تعديل المورد"
                    : "إضافة مورد جديد"}
                </h2>

                <p>
                  أدخل بيانات المورد
                  الأساسية والمالية.
                </p>
              </div>

              <button
                type="button"
                className="supplier-close-button"
                onClick={
                  closeForm
                }
              >
                ×
              </button>
            </div>


            <form
              onSubmit={
                handleSubmit
              }
            >
              <div className="supplier-form-section">
                <h3>
                  المعلومات الأساسية
                </h3>

                <div className="supplier-form-grid">
                  {!editingSupplier && (
                    <label>
                      <span>
                        معرف المورد *
                      </span>

                      <input
                        required
                        value={
                          form.id
                        }
                        placeholder="example-supplier"
                        onChange={
                          event =>
                            updateForm(
                              "id",
                              event.target.value
                                .toLowerCase(),
                            )
                        }
                      />
                    </label>
                  )}

                  <label>
                    <span>
                      كود المورد *
                    </span>

                    <input
                      required
                      value={
                        form.code
                      }
                      onChange={
                        event =>
                          updateForm(
                            "code",
                            event.target.value
                              .toUpperCase(),
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      الاسم *
                    </span>

                    <input
                      required
                      value={
                        form.name
                      }
                      onChange={
                        event =>
                          updateForm(
                            "name",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      الاسم العربي
                    </span>

                    <input
                      value={
                        form.nameAr
                      }
                      onChange={
                        event =>
                          updateForm(
                            "nameAr",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      نوع المورد
                    </span>

                    <select
                      value={
                        form.type
                      }
                      onChange={
                        event =>
                          updateForm(
                            "type",
                            event.target
                              .value as
                              SupplierType,
                          )
                      }
                    >
                      <option value="local">
                        محلي
                      </option>

                      <option value="international">
                        دولي
                      </option>
                    </select>
                  </label>

                  <label>
                    <span>
                      العملة الافتراضية
                    </span>

                    <input
                      list="supplier-currencies"
                      required
                      maxLength={3}
                      value={
                        form.currencyCode
                      }
                      onChange={
                        event =>
                          updateForm(
                            "currencyCode",
                            event.target.value
                              .toUpperCase(),
                          )
                      }
                    />

                    <datalist id="supplier-currencies">
                      {COMMON_CURRENCIES.map(
                        currency => (
                          <option
                            key={
                              currency
                            }
                            value={
                              currency
                            }
                          />
                        ),
                      )}
                    </datalist>
                  </label>

                  <label className="supplier-checkbox-label">
                    <input
                      type="checkbox"
                      checked={
                        form.isActive
                      }
                      onChange={
                        event =>
                          updateForm(
                            "isActive",
                            event.target.checked,
                          )
                      }
                    />

                    <span>
                      المورد نشط
                    </span>
                  </label>
                </div>
              </div>


              <div className="supplier-form-section">
                <h3>
                  معلومات الاتصال
                </h3>

                <div className="supplier-form-grid">
                  <label>
                    <span>
                      مسؤول الاتصال
                    </span>

                    <input
                      value={
                        form.contactPerson
                      }
                      onChange={
                        event =>
                          updateForm(
                            "contactPerson",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      الهاتف
                    </span>

                    <input
                      value={
                        form.phone
                      }
                      onChange={
                        event =>
                          updateForm(
                            "phone",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      هاتف بديل
                    </span>

                    <input
                      value={
                        form.alternatePhone
                      }
                      onChange={
                        event =>
                          updateForm(
                            "alternatePhone",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      البريد الإلكتروني
                    </span>

                    <input
                      type="email"
                      value={
                        form.email
                      }
                      onChange={
                        event =>
                          updateForm(
                            "email",
                            event.target.value,
                          )
                      }
                    />
                  </label>
                </div>
              </div>


              <div className="supplier-form-section">
                <h3>
                  الموقع والعنوان
                </h3>

                <div className="supplier-form-grid">
                  <label>
                    <span>
                      الدولة
                    </span>

                    <input
                      required
                      value={
                        form.country
                      }
                      onChange={
                        event =>
                          updateForm(
                            "country",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      المحافظة
                    </span>

                    <input
                      value={
                        form.province
                      }
                      onChange={
                        event =>
                          updateForm(
                            "province",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      المدينة
                    </span>

                    <input
                      value={
                        form.city
                      }
                      onChange={
                        event =>
                          updateForm(
                            "city",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label className="supplier-full-field">
                    <span>
                      العنوان
                    </span>

                    <input
                      value={
                        form.address
                      }
                      onChange={
                        event =>
                          updateForm(
                            "address",
                            event.target.value,
                          )
                      }
                    />
                  </label>
                </div>
              </div>


              <div className="supplier-form-section">
                <h3>
                  البيانات القانونية
                </h3>

                <div className="supplier-form-grid">
                  <label>
                    <span>
                      الرقم الضريبي
                    </span>

                    <input
                      value={
                        form.taxNumber
                      }
                      onChange={
                        event =>
                          updateForm(
                            "taxNumber",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      رقم التسجيل
                    </span>

                    <input
                      value={
                        form.registrationNumber
                      }
                      onChange={
                        event =>
                          updateForm(
                            "registrationNumber",
                            event.target.value,
                          )
                      }
                    />
                  </label>
                </div>
              </div>


              <div className="supplier-form-section">
                <h3>
                  شروط الدفع
                </h3>

                <div className="supplier-form-grid">
                  <label>
                    <span>
                      نوع الدفع
                    </span>

                    <select
                      value={
                        form.paymentTermsType
                      }
                      onChange={
                        event => {
                          const value =
                            event.target
                              .value as
                              PaymentTermsType;

                          updateForm(
                            "paymentTermsType",
                            value,
                          );

                          if (
                            value ===
                            "cash"
                          ) {
                            updateForm(
                              "paymentTermDays",
                              "0",
                            );
                          }
                        }
                      }
                    >
                      <option value="cash">
                        نقدي
                      </option>

                      <option value="credit">
                        آجل
                      </option>

                      <option value="advance">
                        دفع مقدم
                      </option>

                      <option value="custom">
                        مخصص
                      </option>
                    </select>
                  </label>

                  <label>
                    <span>
                      أيام الاستحقاق
                    </span>

                    <input
                      type="number"
                      min="0"
                      step="1"
                      disabled={
                        form.paymentTermsType ===
                        "cash"
                      }
                      value={
                        form.paymentTermDays
                      }
                      onChange={
                        event =>
                          updateForm(
                            "paymentTermDays",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      حد الائتمان
                    </span>

                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={
                        form.creditLimit
                      }
                      onChange={
                        event =>
                          updateForm(
                            "creditLimit",
                            event.target.value,
                          )
                      }
                    />
                  </label>
                </div>
              </div>


              <div className="supplier-form-section">
                <h3>
                  المعلومات البنكية
                </h3>

                <div className="supplier-form-grid">
                  <label>
                    <span>
                      اسم البنك
                    </span>

                    <input
                      value={
                        form.bankName
                      }
                      onChange={
                        event =>
                          updateForm(
                            "bankName",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      اسم صاحب الحساب
                    </span>

                    <input
                      value={
                        form.bankAccountName
                      }
                      onChange={
                        event =>
                          updateForm(
                            "bankAccountName",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      رقم الحساب
                    </span>

                    <input
                      value={
                        form.bankAccountNumber
                      }
                      onChange={
                        event =>
                          updateForm(
                            "bankAccountNumber",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      IBAN
                    </span>

                    <input
                      value={
                        form.iban
                      }
                      onChange={
                        event =>
                          updateForm(
                            "iban",
                            event.target.value,
                          )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      SWIFT
                    </span>

                    <input
                      value={
                        form.swiftCode
                      }
                      onChange={
                        event =>
                          updateForm(
                            "swiftCode",
                            event.target.value,
                          )
                      }
                    />
                  </label>
                </div>
              </div>


              <div className="supplier-form-section">
                <h3>
                  الملاحظات
                </h3>

                <textarea
                  rows={4}
                  value={
                    form.notes
                  }
                  onChange={
                    event =>
                      updateForm(
                        "notes",
                        event.target.value,
                      )
                  }
                />
              </div>


              <div className="supplier-form-actions">
                <button
                  type="button"
                  className="suppliers-secondary-button"
                  disabled={
                    saving
                  }
                  onClick={
                    closeForm
                  }
                >
                  إلغاء
                </button>

                <button
                  type="submit"
                  className="suppliers-primary-button"
                  disabled={
                    saving
                  }
                >
                  {saving
                    ? "جاري الحفظ..."
                    : editingSupplier
                    ? "حفظ التعديلات"
                    : "إضافة المورد"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}